import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {FormField} from 'shells/form/form-field';
import {GridSelector} from 'shells/grid-selector/grid-selector';
import {Popover} from 'shells/popover/popover';
import {PopoverForm} from 'shells/popover-form/popover-form';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import type {ActivityTypeIcon} from 'nutshell-graphql-types';

import {bustManagedCollectionCache} from '../../../util/bust-managed-collection-cache';

import {ACTIVITY_TYPE_ICONS_ITEMS} from './consts';
import {useCreateActivityType} from './graphql/hooks/use-create-activity-type';
import {useUpdateActivityType} from './graphql/hooks/use-update-activity-type';
import {suggestIconForActivityType} from './helpers';

type Props = {
    anchor: HTMLElement | null | undefined;
    onClose: () => void;
    activityTypeApiId: string | null | undefined;
    name: string | null | undefined;
    icon: ActivityTypeIcon | null | undefined;
};

export function EditCreateActivityTypePopover(props: Props) {
    const [name, setName] = React.useState<string>(props.name || '');
    const [selectedIcon, setSelectedIcon] = React.useState<ActivityTypeIcon | null | undefined>(
        props.icon || null
    );
    const [suggestedIcon, setSuggestedIcon] = React.useState<ActivityTypeIcon | null | undefined>(
        null
    );
    const appliedIcon = selectedIcon || suggestedIcon || 'DEFAULT';

    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const {createActivityType, isCreating} = useCreateActivityType();
    const {updateActivityType, isLoading} = useUpdateActivityType();
    const isCreatingNewActivityType = !props.activityTypeApiId;

    React.useEffect(() => {
        if (isCreatingNewActivityType) {
            const icon = suggestIconForActivityType(name);
            setSuggestedIcon(icon);
        }
    }, [name, isCreatingNewActivityType, setSuggestedIcon]);

    return (
        <Popover anchor={props.anchor} location='top' onBlur={props.onClose}>
            <PopoverForm
                title={isCreatingNewActivityType ? 'Create activity type' : 'Edit activity type'}
                saveButtonText={isCreatingNewActivityType ? 'Create activity type' : 'Save changes'}
                saveButtonSavingText={isCreatingNewActivityType ? 'Creating...' : 'Saving...'}
                saveButtonSuccessText={isCreatingNewActivityType ? 'Done' : 'Saved'}
                width={414}
                onClose={props.onClose}
                saveButtonDisabled={!name}
                isSaving={isCreating || isLoading}
                errorSaving={Boolean(errorMessage)}
                onSubmit={() => {
                    setErrorMessage(null);
                    if (props.activityTypeApiId) {
                        updateActivityType({
                            id: props.activityTypeApiId,
                            name,
                            icon: appliedIcon,
                        })
                            .then(() => {
                                props.onClose();

                                // Activity types are pulled into redux on the initial load of the app.
                                // We need to fetch them again whenever we update a new activity type.
                                bustManagedCollectionCache('ActivityTypes');
                            })
                            .catch((e) => {
                                setErrorMessage(
                                    safelyGetGraphQLErrorMessage(e) ||
                                        'We encountered a problem while updating the activity type.'
                                );
                            });
                    } else {
                        setErrorMessage(null);
                        createActivityType({name, icon: appliedIcon})
                            .then(() => {
                                props.onClose();

                                // Activity types are pulled into redux on the initial load of the app.
                                // We need to fetch them again whenever we create a new activity type.
                                bustManagedCollectionCache('ActivityTypes');
                            })
                            .catch((e) => {
                                setErrorMessage(
                                    safelyGetGraphQLErrorMessage(e) ||
                                        'We encountered a problem while creating the activity type.'
                                );
                            });
                    }
                }}
            >
                <FormField
                    innerId='name'
                    label={'Name'}
                    isRequired={true}
                    errorMessage={errorMessage || ''}
                >
                    <TextField
                        focusOnMount={true}
                        selectOnFocus={true}
                        placeholder='Phone call'
                        value={name || ''}
                        onChange={(enteredName) => setName(enteredName)}
                        hasError={Boolean(errorMessage)}
                    />
                </FormField>
                {/* @ts-expect-error */}
                <div spacing={8} className='flex flex-dir-col gap-8 pb-16'>
                    <Body size='normal' color='medium'>
                        Icon
                    </Body>
                    <GridSelector
                        items={ACTIVITY_TYPE_ICONS_ITEMS}
                        selectedItemId={appliedIcon}
                        onSelectItem={(itemId) => {
                            // @ts-expect-error
                            setSelectedIcon(itemId);
                        }}
                        numColumns={6}
                    />
                </div>
            </PopoverForm>
        </Popover>
    );
}
